import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Popover,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  InfoOutlined as InfoIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { getMyRecords, getSchoolReport, TeacherAttendanceResponse } from "../../services/teacherAttendanceService";
import { useTranslation } from "react-i18next";
import { parseISO, format } from "date-fns";

const parseSafeISO = (dateVal: any): Date => {
  if (!dateVal) return new Date();
  
  if (dateVal instanceof Date) {
    return dateVal;
  }

  if (typeof dateVal === 'number') {
    if (dateVal < 10000000000) {
      return new Date(dateVal * 1000);
    }
    return new Date(dateVal);
  }
  
  if (Array.isArray(dateVal)) {
    const [year, month, day, hour = 0, minute = 0, second = 0, nano = 0] = dateVal;
    const ms = Math.floor(nano / 1000000);
    return new Date(year, month - 1, day, hour, minute, second, ms);
  }
  
  const dateStr = String(dateVal).trim();

  // If it's a numeric string (timestamp)
  if (/^\d+(\.\d+)?$/.test(dateStr)) {
    const num = Number(dateStr);
    if (num < 10000000000) {
      return new Date(num * 1000);
    }
    return new Date(num);
  }

  // If there's a space between date and time, replace the first space with 'T'
  let normalized = dateStr;
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(normalized)) {
    normalized = normalized.replace(/\s+/, 'T');
  }

  const date = new Date(normalized);
  if (!isNaN(date.getTime())) {
    return date;
  }

  // Fallback: manual parsing for ISO-like formats
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(.*)$/);
  if (match) {
    const [_, y, M, d, h, m, s, msVal, tz] = match;
    const year = parseInt(y, 10);
    const month = parseInt(M, 10) - 1;
    const day = parseInt(d, 10);
    const hour = parseInt(h, 10);
    const minute = parseInt(m, 10);
    const second = parseInt(s, 10);
    const ms = msVal ? parseInt(msVal.substring(0, 3).padEnd(3, '0'), 10) : 0;

    if (tz) {
      const tzClean = tz.trim();
      if (tzClean === 'Z') {
        return new Date(Date.UTC(year, month, day, hour, minute, second, ms));
      }
      const tzMatch = tzClean.match(/^([+-])(\d{2}):?(\d{2})?$/);
      if (tzMatch) {
        const [__, sign, tzH, tzM = '0'] = tzMatch;
        const offsetMinutes = parseInt(tzH, 10) * 60 + parseInt(tzM, 10);
        const offsetMs = offsetMinutes * 60 * 1000 * (sign === '+' ? 1 : -1);
        const utcTime = Date.UTC(year, month, day, hour, minute, second, ms);
        return new Date(utcTime - offsetMs);
      }
    }
    return new Date(year, month, day, hour, minute, second, ms);
  }

  return new Date(dateStr);
};

const formatDateString = (dateVal: any): string => {
  if (!dateVal) return "";
  if (Array.isArray(dateVal)) {
    const [year, month, day] = dateVal;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  const dateStr = String(dateVal).trim();
  if (dateStr.includes(",")) {
    const parts = dateStr.split(",");
    if (parts.length === 3) {
      return `${parts[0]}-${parts[1].padStart(2, "0")}-${parts[2].padStart(2, "0")}`;
    }
  }
  return dateStr.split("T")[0];
};

interface TeacherAttendanceCalendarProps {
  staffId?: number; // Optional: fallback to current user in service
  month?: number;
  year?: number;
}

export const TeacherAttendanceCalendar: React.FC<TeacherAttendanceCalendarProps> = ({
  staffId,
  month: initialMonth,
  year: initialYear,
}) => {
  const { t } = useTranslation();
  const today = new Date();
  const [now, setNow] = useState<Date>(new Date());
  
  const [month, setMonth] = useState<number>(initialMonth || today.getMonth() + 1); // 1-indexed
  const [year, setYear] = useState<number>(initialYear || today.getFullYear());
  const [records, setRecords] = useState<TeacherAttendanceResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const getDisplayHours = (rec: TeacherAttendanceResponse) => {
    if (!rec) return 0;
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const isToday = formatDateString(rec.date) === todayStr;
    if (isToday && !rec.checkOutTime) {
      const checkInDate = parseSafeISO(rec.checkInTime);
      const elapsedSecs = Math.max(0, (now.getTime() - checkInDate.getTime()) / 1000);
      return elapsedSecs / 3600;
    }
    return rec.totalHours ?? 0;
  };

  // Sync prop changes
  useEffect(() => {
    if (initialMonth) setMonth(initialMonth);
  }, [initialMonth]);

  useEffect(() => {
    if (initialYear) setYear(initialYear);
  }, [initialYear]);
  
  // Popover state
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<TeacherAttendanceResponse | null>(null);

  // Month options
  const months = [
    { value: 1, label: t("January") || "January" },
    { value: 2, label: t("February") || "February" },
    { value: 3, label: t("March") || "March" },
    { value: 4, label: t("April") || "April" },
    { value: 5, label: t("May") || "May" },
    { value: 6, label: t("June") || "June" },
    { value: 7, label: t("July") || "July" },
    { value: 8, label: t("August") || "August" },
    { value: 9, label: t("September") || "September" },
    { value: 10, label: t("October") || "October" },
    { value: 11, label: t("November") || "November" },
    { value: 12, label: t("December") || "December" },
  ];

  // Year options
  const years = Array.from({ length: 5 }, (_, i) => today.getFullYear() - 2 + i);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      if (staffId) {
        // Calculate days in the selected month
        const daysInMonth = new Date(year, month, 0).getDate();
        const fromDate = `${year}-${String(month).padStart(2, "0")}-01`;
        const toDate = `${year}-${String(month).padStart(2, "0")}-${String(daysInMonth).padStart(2, "0")}`;
        
        // Fetch up to 1000 records for the month and filter for this staff member
        const report = await getSchoolReport(fromDate, toDate, 0, 1000);
        const filtered = report.content.filter((r) => r.staffId === staffId);
        setRecords(filtered);
      } else {
        const data = await getMyRecords(month, year);
        setRecords(data);
      }
    } catch (err) {
      console.error("Error loading calendar logs", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, [month, year]);

  useEffect(() => {
    const handleUpdate = () => {
      fetchRecords();
    };
    window.addEventListener("attendance-updated", handleUpdate);
    return () => {
      window.removeEventListener("attendance-updated", handleUpdate);
    };
  }, [month, year]);

  const handleCellClick = (event: React.MouseEvent<HTMLElement>, rec: TeacherAttendanceResponse) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecord(rec);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setSelectedRecord(null);
  };

  const open = Boolean(anchorEl);

  // Generate calendar days matrix
  const getDaysInMonth = (m: number, y: number) => new Date(y, m, 0).getDate();
  const getFirstDayOffset = (m: number, y: number) => {
    // 0 = Sunday, 1 = Monday, ...
    const day = new Date(y, m - 1, 1).getDay();
    // Map to Mon-Sun: Mon=0, Tue=1, ..., Sun=6
    return day === 0 ? 6 : day - 1;
  };

  const totalDays = getDaysInMonth(month, year);
  const startOffset = getFirstDayOffset(month, year);
  
  const cells: (number | null)[] = [];
  // Pad beginning
  for (let i = 0; i < startOffset; i++) {
    cells.push(null);
  }
  // Fill month days
  for (let i = 1; i <= totalDays; i++) {
    cells.push(i);
  }

  // Create date lookup map
  const recordMap = new Map<string, TeacherAttendanceResponse>();
  records.forEach((rec) => {
    recordMap.set(formatDateString(rec.date), rec);
  });

  // Color config mapping
  const getStatusColors = (status: string) => {
    switch (status) {
      case "PRESENT": return { bg: "#e8f5e9", border: "#4caf50", dot: "#4caf50" };
      case "LATE": return { bg: "#fff3e0", border: "#ff9800", dot: "#ff9800" };
      case "HALF_DAY": return { bg: "#fffde7", border: "#fdd835", dot: "#fdd835" };
      case "LEAVE": return { bg: "#e3f2fd", border: "#2196f3", dot: "#2196f3" };
      case "ABSENT": return { bg: "#ffebee", border: "#f44336", dot: "#f44336" };
      default: return { bg: "#eeeeee", border: "#9e9e9e", dot: "#9e9e9e" }; // INCOMPLETE
    }
  };

  // Summary counts
  const summary = {
    present: 0,
    late: 0,
    halfDay: 0,
    absent: 0,
    leave: 0,
    hours: 0.0,
  };

  records.forEach((rec) => {
    summary.hours += getDisplayHours(rec);
    if (rec.status === "PRESENT") summary.present++;
    else if (rec.status === "LATE") summary.late++;
    else if (rec.status === "HALF_DAY") summary.halfDay++;
    else if (rec.status === "ABSENT") summary.absent++;
    else if (rec.status === "LEAVE") summary.leave++;
  });

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return "--:--";
    try {
      return format(parseSafeISO(timeStr), "hh:mm a");
    } catch {
      return "--:--";
    }
  };

  return (
    <Box sx={{ width: "100%", mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        
        {/* Month & Year Selectors */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, flexWrap: "wrap", gap: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            {t("Monthly History")}
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{t("month")}</InputLabel>
              <Select
                value={month}
                label={t("month")}
                onChange={(e) => setMonth(Number(e.target.value))}
              >
                {months.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>{t("year")}</InputLabel>
              <Select
                value={year}
                label={t("year")}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Calendar Grid Header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 1,
                textAlign: "center",
                fontWeight: "bold",
                mb: 1,
                color: "text.secondary",
                fontSize: "0.85rem",
              }}
            >
              <div>{t("Mon") || "Mon"}</div>
              <div>{t("Tue") || "Tue"}</div>
              <div>{t("Wed") || "Wed"}</div>
              <div>{t("Thu") || "Thu"}</div>
              <div>{t("Fri") || "Fri"}</div>
              <div>{t("Sat") || "Sat"}</div>
              <div>{t("Sun") || "Sun"}</div>
            </Box>

            {/* Calendar Cells Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 1.5,
              }}
            >
              {cells.map((day, index) => {
                if (day === null) {
                  return <Box key={`empty-${index}`} sx={{ minHeight: "80px", bgcolor: "transparent" }} />;
                }

                // Check for record on this date
                const dateStr = `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
                const rec = recordMap.get(dateStr);
                const colors = rec ? getStatusColors(rec.status) : null;
                const isWeekend = (index % 7 === 5 || index % 7 === 6); // Sat or Sun

                return (
                  <Box
                    key={`day-${day}`}
                    onClick={(e) => rec && handleCellClick(e, rec)}
                    sx={{
                      minHeight: "85px",
                      p: 1,
                      border: "1px solid",
                      borderColor: colors ? colors.border : isWeekend ? "#eee" : "#e0e0e0",
                      borderRadius: 2,
                      bgcolor: colors ? colors.bg : isWeekend ? "#fafafb" : "#ffffff",
                      cursor: rec ? "pointer" : "default",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      transition: "all 0.2s ease",
                      position: "relative",
                      "&:hover": rec ? {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        transform: "translateY(-1px)",
                      } : {},
                    }}
                  >
                    {/* Day Number */}
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color={isWeekend && !colors ? "text.secondary" : "text.primary"}
                    >
                      {day}
                    </Typography>

                    {/* Cell Status Details */}
                    {rec && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: colors?.dot }} />
                          <Typography variant="caption" sx={{ fontSize: "0.68rem", fontWeight: "bold" }}>
                            {formatTime(rec.checkInTime)}
                          </Typography>
                        </Box>
                        {getDisplayHours(rec) > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", pl: 1.5 }}>
                            {getDisplayHours(rec).toFixed(2)} hrs
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Weekend tag fallback */}
                    {!rec && isWeekend && (
                      <Typography variant="caption" color="text.disabled" align="center" sx={{ fontSize: "0.65rem" }}>
                        {t("Off")}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Calendar Summary Row */}
            <Box
              sx={{
                mt: 4,
                p: 2.5,
                bgcolor: "#f8f9fb",
                borderRadius: 2.5,
                border: "1px solid #eef0f2",
                display: "flex",
                justifyContent: "space-around",
                flexWrap: "wrap",
                gap: 2,
                textAlign: "center",
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t("Present")}</Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">{summary.present}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t("Late")}</Typography>
                <Typography variant="body1" fontWeight="bold" color="warning.main">{summary.late}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t("Half Day")}</Typography>
                <Typography variant="body1" fontWeight="bold" color="warning.dark">{summary.halfDay}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t("Absent")}</Typography>
                <Typography variant="body1" fontWeight="bold" color="error.main">{summary.absent}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t("Leave")}</Typography>
                <Typography variant="body1" fontWeight="bold" color="info.main">{summary.leave}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t("Total Hours Worked")}</Typography>
                <Typography variant="body1" fontWeight="bold" color="primary.main">{summary.hours.toFixed(1)} hrs</Typography>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* Popover detailed day records */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: { p: 3, maxWidth: 300, borderRadius: 2, boxShadow: 4 },
        }}
      >
        {selectedRecord && (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {formatDateString(selectedRecord.date)}
              </Typography>
              <Chip
                label={selectedRecord.status ? selectedRecord.status.replace(/_/g, " ") : ""}
                color={getStatusColors(selectedRecord.status).dot === "#4caf50" ? "success" : "warning"}
                size="small"
              />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t("Check-In")}</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatTime(selectedRecord.checkInTime)}
                </Typography>
              </Box>

              {/* Show Last Check-In only if teacher checked in more than once */}
              {selectedRecord.lastCheckInTime &&
                formatTime(selectedRecord.lastCheckInTime) !== formatTime(selectedRecord.checkInTime) && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">{t("Last Check-In")}</Typography>
                  <Typography variant="body2" fontWeight="bold" color="warning.main">
                    {formatTime(selectedRecord.lastCheckInTime)}
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t("Check-Out")}</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {selectedRecord.checkOutTime ? formatTime(selectedRecord.checkOutTime) : "--:--"}
                </Typography>
              </Box>

              {/* Show Last Check-Out only if it differs from checkOutTime */}
              {selectedRecord.lastCheckOutTime &&
                formatTime(selectedRecord.lastCheckOutTime) !== formatTime(selectedRecord.checkOutTime) && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">{t("Last Check-Out")}</Typography>
                  <Typography variant="body2" fontWeight="bold" color="warning.main">
                    {formatTime(selectedRecord.lastCheckOutTime)}
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t("Logged Hours")}</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {selectedRecord ? `${getDisplayHours(selectedRecord).toFixed(2)} hrs` : "0 hrs"}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t("Verification Channel")}</Typography>
                <Typography variant="body2" fontWeight="bold" color="primary">
                  {selectedRecord.checkInMethod} {selectedRecord.geoVerified ? "(GPS Verified)" : "(Unverified / QR)"}
                </Typography>
              </Box>

              {selectedRecord.overrideNote && (
                <Box sx={{ mt: 1, p: 1, bgcolor: "#fff3e0", borderRadius: 1 }}>
                  <Typography variant="caption" color="warning.dark" display="block">
                    {t("Admin Override Note")}:
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedRecord.overrideNote}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default TeacherAttendanceCalendar;
